import { notification } from 'antd';
import { AppModal } from '@components/form/Modal';
import { useModal } from '@shared/hooks/use-modal';
import { useCollectPaymentMutation, useEstimateRenewFailedSubscriptionV2Query } from '@/types/generated/graphql';
import { PaymentMethodsList } from '@modules/settings/subscription/components/payment-methods-list';
import { usePaymentMethodsList } from '@modules/settings/subscription/hooks/use-payment-methods-list';
import { centsToDollar, formatAmount } from '@modules/settings/subscription/utils/utils';
import { useTranslation } from 'react-i18next';
import { capitalizeFirst } from '@shared/utils/capitalize';
import { InrPaymentAlert } from '@components/inr-payment-alert';

type Iprops = {
  paymentModal: ReturnType<typeof useModal>;
};

const SuspensionPaymentDueModal = ({ paymentModal }: Iprops) => {
  const { data } = useEstimateRenewFailedSubscriptionV2Query();
  const estimatedAmount = data?.estimateRenewFailedSubscriptionV2?.data?.estimatedAmount;
  const paymentMethodsList = usePaymentMethodsList();
  const { t } = useTranslation();
  const [collect, { loading }] = useCollectPaymentMutation({
    onCompleted: (data) => {
      notification.success({ message: data.collectPayment?.message });
      paymentModal.handleClose();
    },
    onError: (error) => {
      notification.error({ message: error.message });
    },
    update: (cache, { data }) => {
      if (data) {
        cache.modify({
          fields: {
            workspaceAuthMeV2: () => {},
          },
        });
      }
    },
  });
  const handlePay = async () => {
    await collect({
      variables: {
        paymentSourceId: paymentMethodsList.paymentMethod?.id || '',
      },
    });
  };

  return (
    <div>
      <AppModal
        open={paymentModal.open}
        onCancel={paymentModal.handleClose}
        classNames={{ content: '!max-w-[420px]' }}
        centered
        title={
          <AppModal.Header>
            <AppModal.Title>{capitalizeFirst(t('choose_your_payment_method'))}</AppModal.Title>
            <AppModal.SubTitle>{t('select_a_payment_method_to_confirm_purchase')}</AppModal.SubTitle>
          </AppModal.Header>
        }
        footer={
          <AppModal.Footer className="flex flex-col">
            <AppModal.Button className="w-full capitalize!" onClick={handlePay} type="primary" loading={loading}>
              {t('pay_now')}
            </AppModal.Button>
            <PaymentMethodsList.CompliantLabel />
          </AppModal.Footer>
        }
      >
        <AppModal.Body>
          <PaymentMethodsList
            loading={paymentMethodsList.listSubscriptionResult?.loading}
            paymentCards={paymentMethodsList.paymentCards}
            paymentPaypal={paymentMethodsList.paymentPaypal}
            selected={paymentMethodsList.paymentMethod}
            onChange={paymentMethodsList.setPaymentMethod}
          />
          <div className="border border-gray-400 rounded-xl p-4 my-6">
            <div className="flex justify-between items-center">
              <div className="text-text-700 text-[13px] capitalize!">{t('total_amount')}</div>
              <div className="text-text-800 text-[15px]">{formatAmount(centsToDollar(estimatedAmount))}</div>
            </div>
          </div>
          <InrPaymentAlert flow="suspension-payment" className="mb-2" />
        </AppModal.Body>
      </AppModal>
    </div>
  );
};

export default SuspensionPaymentDueModal;
